import { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import {
    AllInclusiveRounded as InfinityIcon,
    ArrowForwardRounded as ArrowIcon,
    AutoAwesomeRounded as SparkleIcon,
    CardGiftcardRounded as GiftIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useUserStore } from '@/store';
import { dialogOpen } from '@/modules/MuiDialog';
import { requestNicepayAuth } from '@/utils/nicepayAuth';
import { useAutomationLimit, useAutomationUsage } from '@/hooks/queries/dm-automation';
import { refillAutomationPass } from '@/services/dmAutomation.service';
import {
    createPassOrder,
    confirmPassOrder,
    getPassPackages,
    type AutomationPassPackage,
} from '@/services/automationPass.service';
import type { AutomationLimit, AutomationUsageItem } from '@/types/dmAutomation';
import { ACCENT, ACCENT_HOVER, ACCENT_TINT, PageHeader, TRIGGER_LABEL } from './_shared';

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

/** 섹션 헤더 (스텝 번호 + 제목) */
const SectionTitle = ({ step, title, desc }: { step: number; title: string; desc?: string }) => (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.75 }}>
        <Box
            sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: ACCENT_TINT,
                color: ACCENT,
                fontSize: 12,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            {step}
        </Box>
        <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{title}</Typography>
            {desc && (
                <Typography variant="body2" color="text.secondary">
                    {desc}
                </Typography>
            )}
        </Box>
    </Stack>
);

/** 이용권 팩 카드 */
const PackCard = ({
    pack,
    onClick,
    loading,
}: {
    pack: AutomationPassPackage;
    onClick: () => void;
    loading?: boolean;
}) => (
    <Box
        sx={{
            flex: '1 1 220px',
            minWidth: 220,
            bgcolor: '#fff',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 3,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        }}
    >
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {pack.name}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: ACCENT }}>
                +{pack.credits}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                개
            </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
            {won(pack.price)}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
            onClick={onClick}
            disabled={loading}
            variant="contained"
            disableElevation
            sx={{ mt: 1, bgcolor: ACCENT, fontWeight: 700, '&:hover': { bgcolor: ACCENT_HOVER } }}
        >
            {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : '충전하기'}
        </Button>
    </Box>
);

/** 무한리필 무료 — "무조건 무료" 강조 히어로 (free-refill ON 시 판매 카드 대신 노출) */
const EventHero = ({
    refillTarget,
    remaining,
    claiming,
    onClaim,
}: {
    refillTarget: number; // 리필 후 남은 개수(= 500 + 유료)
    remaining: number;
    claiming: boolean;
    onClaim: () => void;
}) => {
    const target = refillTarget.toLocaleString();
    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 4,
                p: { xs: 3, md: 4.5 },
                color: '#fff',
                background: 'linear-gradient(135deg, #241a17 0%, #4a2c22 52%, #b6472a 128%)',
                boxShadow: '0 16px 44px rgba(180,60,30,0.28)',
            }}
        >
            {/* 장식 글로우 */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -70,
                    right: -50,
                    width: 240,
                    height: 240,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${ACCENT}66, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -90,
                    left: -40,
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.06), transparent 70%)',
                    pointerEvents: 'none',
                }}
            />

            <Stack spacing={2.5} sx={{ position: 'relative' }}>
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                    <Chip
                        icon={<GiftIcon sx={{ fontSize: 16 }} />}
                        label="무한리필 · 무조건 무료"
                        sx={{
                            bgcolor: 'rgba(255,255,255,0.16)',
                            color: '#fff',
                            fontWeight: 800,
                            '& .MuiChip-icon': { color: '#fff' },
                        }}
                    />
                </Stack>

                <Box>
                    <Typography sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 900, lineHeight: 1.18, letterSpacing: '-0.02em' }}>
                        자동DM 이용권,
                        <br />
                        <Box component="span" sx={{ color: '#FFC9B4' }}>무조건 무료</Box>로 무한 리필
                    </Typography>
                    <Typography sx={{ mt: 1.5, fontSize: { xs: 14, md: 15.5 }, color: 'rgba(255,255,255,0.82)', lineHeight: 1.65, maxWidth: 560 }}>
                        다른 플랫폼에선 자동화, 유료로 쓰셨어요?{' '}
                        <Box component="span" sx={{ fontWeight: 900, color: '#FFC9B4' }}>소듀</Box>에서는{' '}
                        <Box component="span" sx={{ fontWeight: 800, color: '#fff' }}>무제한 리필 무료</Box>입니다!
                        <Box component="span" sx={{ fontWeight: 800, color: '#fff' }}> 언제든 몇 번이든</Box> 다시 채울 수 있어요.
                    </Typography>
                </Box>

                {/* 리필 시각화 */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    flexWrap="wrap"
                    sx={{
                        py: 1.25,
                        px: 2,
                        borderRadius: 3,
                        alignSelf: 'flex-start',
                        maxWidth: '100%',
                        bgcolor: 'rgba(255,255,255,0.10)',
                        border: '1px solid rgba(255,255,255,0.14)',
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>지금 남은</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{remaining.toLocaleString()}개</Typography>
                    </Box>
                    <ArrowIcon sx={{ opacity: 0.7 }} />
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>리필 후</Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 900, color: '#FFC9B4' }}>{target}개</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                        <InfinityIcon sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>무제한 리필</Typography>
                    </Stack>
                </Stack>

                <Box>
                    <Button
                        onClick={onClaim}
                        disabled={claiming}
                        size="large"
                        startIcon={claiming ? <CircularProgress size={18} sx={{ color: ACCENT }} /> : <SparkleIcon />}
                        sx={{
                            bgcolor: '#fff',
                            color: ACCENT,
                            fontWeight: 900,
                            fontSize: 16,
                            px: 4,
                            py: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            boxShadow: '0 10px 26px rgba(0,0,0,0.28)',
                            transition: 'transform 120ms ease, box-shadow 120ms ease',
                            '&:hover': { bgcolor: '#fff', transform: 'translateY(-2px)', boxShadow: '0 14px 32px rgba(0,0,0,0.34)' },
                            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.7)', color: ACCENT },
                        }}
                    >
                        무료로 리필하기
                    </Button>
                    <Typography sx={{ mt: 1.5, fontSize: 12.5, color: 'rgba(255,255,255,0.62)' }}>
                        카드 등록 없이 바로 채워져요 · 눌러도 {target}개를 넘지 않고 {target}개까지만 채워져요
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
};

/** 내역 한 줄 (충전 +N / 자동화 생성 −1 / 무한리필 +N) */
const UsageRow = ({ item }: { item: AutomationUsageItem }) => {
    const isCharge = item.kind === 'charge';
    const isRefill = item.kind === 'refill';
    const isUsage = item.kind === 'usage';
    const isPlus = isCharge || isRefill;
    const isDeleted = item.status === 'DELETED';
    const typeLabel = item.triggerType ? TRIGGER_LABEL[item.triggerType] : null;

    const title = isCharge ? '충전' : isRefill ? '무한리필' : item.name || '이름없는 자동화';
    const caption = isCharge
        ? item.name
        : isRefill
        ? `리필${item.remaining != null ? ` · 잔여 ${item.remaining.toLocaleString()}` : ''}`
        : typeLabel ?? '자동화 생성';

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.25 }}>
            <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {title}
                    </Typography>
                    {isUsage &&
                        (isDeleted ? (
                            <Chip
                                label="삭제됨"
                                size="small"
                                sx={{ height: 18, fontSize: 11, bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary' }}
                            />
                        ) : (
                            <Chip
                                label="이용중"
                                size="small"
                                sx={{ height: 18, fontSize: 11, bgcolor: ACCENT_TINT, color: ACCENT }}
                            />
                        ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    {caption} · {item.createdAt?.slice(0, 10)}
                </Typography>
            </Box>
            <Typography
                variant="body2"
                sx={{ fontWeight: 800, color: isPlus ? ACCENT : 'text.primary', whiteSpace: 'nowrap' }}
            >
                {isPlus ? `+${item.amount}` : `이용권 ${item.amount}`}
            </Typography>
        </Stack>
    );
};

/**
 * 자동DM 이용권 화면.
 * 소모제: 자동화 생성 시 이용권 1개 차감(삭제·비활성화해도 복구 없음).
 * 요약/내역은 studio 통합 엔드포인트(GET /automations/usage), 충전은 mall 2-step.
 */
const AutomationPassPage = () => {
    const user = useUserStore();
    const qc = useQueryClient();
    const usageQ = useAutomationUsage();
    const limitQ = useAutomationLimit();
    const packagesQ = useQuery({ queryKey: ['autodm', 'pass', 'packages'], queryFn: getPassPackages });

    const [purchasingCode, setPurchasingCode] = useState<string | null>(null);
    const [claiming, setClaiming] = useState(false);

    const limit: AutomationLimit | undefined = limitQ.data;
    const refillTo = limit?.refillTo ?? 500; // 무료 바닥값(500)
    const refillCeiling = refillTo + (limit?.extraPass ?? 0); // 리필 후 남은 개수(= 500 + 유료)

    // 실제 무한리필 — studio, 바디 없음. 남은 개수를 (500+유료)로 복구
    const runRefill = async () => {
        if (claiming) return;
        setClaiming(true);
        try {
            const res = await refillAutomationPass();
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['dm-automation', 'usage'] }),
                qc.invalidateQueries({ queryKey: ['dm-automation', 'limit'] }),
                qc.invalidateQueries({ queryKey: ['dm-automation', 'list'] }),
            ]);
            toast.success(
                res.restored > 0
                    ? `이용권 ${res.restored.toLocaleString()}개가 충전됐어요`
                    : '이미 최대치까지 보유 중이에요',
            );
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || '리필에 실패했어요');
        } finally {
            setClaiming(false);
        }
    };

    // "무료로 리필하기" → 확인 다이얼로그 → runRefill
    const handleRefill = () => {
        if (claiming) return;
        const remaining = limit?.remaining ?? usageQ.data?.summary?.remaining ?? 0;
        dialogOpen({
            message: `현재 <b>${remaining.toLocaleString()}개</b> → 리필 후 <b>${refillCeiling.toLocaleString()}개</b><br/>지금 리필하시겠어요?`,
            confirmText: '리필하기',
            closeText: '취소',
            isAllowHtml: true,
            onConfirmed: runRefill,
        });
    };

    // mall 2-step 결제: ① 주문 생성 → 결제창(requestNicepayAuth) → ② confirm(승인·적립)
    const handleCharge = async (pack: AutomationPassPackage) => {
        if (purchasingCode) return;
        setPurchasingCode(pack.code);
        try {
            const init = await createPassOrder(pack.code);
            await requestNicepayAuth(
                {
                    MID: init.MID,
                    Amt: init.Amt,
                    GoodsName: init.GoodsName,
                    Moid: init.Moid,
                    EdiDate: init.EdiDate,
                    SignData: init.SignData,
                    PayMethod: 'CARD',
                    BuyerName: user.nickname || '',
                    BuyerTel: user.phone_number || '',
                    BuyerEmail: user.email || '',
                    ReturnURL: '',
                },
                {
                    onClose: () => setPurchasingCode(null),
                    onSuccess: async (authResult: any) => {
                        try {
                            const result = await confirmPassOrder({
                                packageCode: init.packageCode,
                                TID: authResult.TID || authResult.TxTid,
                                AuthToken: authResult.AuthToken,
                                MID: authResult.MID || init.MID,
                                Amt: String(authResult.Amt || init.Amt),
                                EdiDate: authResult.EdiDate || init.EdiDate,
                                NextAppURL: authResult.NextAppURL,
                            });
                            await Promise.all([
                                // 상한 증가 반영 — 요약/내역/게이팅/목록
                                qc.invalidateQueries({ queryKey: ['dm-automation', 'usage'] }),
                                qc.invalidateQueries({ queryKey: ['dm-automation', 'limit'] }),
                                qc.invalidateQueries({ queryKey: ['dm-automation', 'list'] }),
                            ]);
                            toast.success(
                                result.purchased > 0
                                    ? `이용권 ${result.purchased.toLocaleString()}개가 충전됐어요.`
                                    : '이미 처리된 결제예요.',
                            );
                        } catch (e: any) {
                            toast.error(e?.message || '결제 승인 처리 중 오류가 발생했습니다.');
                        } finally {
                            setPurchasingCode(null);
                        }
                    },
                    onError: (error: any) => {
                        const msg = error?.message || (typeof error === 'string' ? error : '결제 인증에 실패했습니다.');
                        if (msg !== '사용자가 결제를 취소했습니다.') toast.error(msg);
                        setPurchasingCode(null);
                    },
                },
            );
        } catch (e: any) {
            toast.error(e?.message || '결제를 시작하지 못했습니다.');
            setPurchasingCode(null);
        }
    };

    const packages = packagesQ.data ?? [];
    const summary = usageQ.data?.summary;
    const usageItems = usageQ.data?.itemList ?? [];

    // studio limit.refillEventActive 로 이벤트 판별 → 판매 카드 숨기고 무한리필 히어로만 노출
    const isEvent = limit?.refillEventActive === true;

    return (
        <Box sx={{ maxWidth: 920 }}>
            <PageHeader
                title="이용권"
                desc="자동화를 만들 수 있는 이용권을 충전하세요. 구매한 이용권은 만료되지 않아요."
            />

            {/* 이용권 요약 (사용한 / 남은) — 앱 화면과 동일 레이아웃 */}
            <Box
                sx={{
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    p: { xs: 2.5, md: 3 },
                    mb: 2.5,
                    background: `linear-gradient(135deg, ${ACCENT_TINT}, #fff 60%)`,
                }}
            >
                {usageQ.isLoading ? (
                    <CircularProgress size={24} sx={{ color: ACCENT }} />
                ) : (
                    <>
                        <Stack direction="row" alignItems="center" divider={<Divider orientation="vertical" flexItem />} spacing={3}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    사용한 이용권
                                </Typography>
                                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                        {summary?.used ?? 0}
                                    </Typography>
                                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        / {summary?.totalLimit ?? 0}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    남은 이용권
                                </Typography>
                                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                    <Typography variant="h4" sx={{ fontWeight: 900, color: ACCENT }}>
                                        {summary?.remaining ?? 0}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        개
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                            자동화를 만들 때마다 이용권 1개가 차감돼요. <b>삭제하거나 꺼도 사용한 이용권은 복구되지 않으니</b>{' '}
                            신중히 만들어 주세요. (기존 자동화 수정은 차감 없음)
                        </Typography>
                    </>
                )}
            </Box>

            {/* 이용권 충전 (이벤트 ON: 무료 리필권 히어로 / OFF: 판매 카드) */}
            <Box sx={{ mb: 3 }}>
                <SectionTitle
                    step={1}
                    title={isEvent ? '무한리필 무조건 무료' : '이용권 충전'}
                    desc={isEvent ? '이용권을 무료로 채워 자동화를 마음껏 만드세요.' : '필요한 만큼 한 번에 충전해요.'}
                />
                {(isEvent ? limitQ.isLoading : packagesQ.isLoading) ? (
                    <CircularProgress size={22} sx={{ color: ACCENT }} />
                ) : isEvent ? (
                    <EventHero
                        refillTarget={refillCeiling}
                        remaining={limit?.remaining ?? summary?.remaining ?? 0}
                        claiming={claiming}
                        onClaim={handleRefill}
                    />
                ) : packages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        이용 가능한 이용권 상품이 없어요.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {packages.map((p) => (
                            <PackCard
                                key={p.code}
                                pack={p}
                                loading={purchasingCode === p.code}
                                onClick={() => handleCharge(p)}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {/* 이용권 내역 (충전 + 사용 통합) */}
            <Box>
                <SectionTitle step={2} title="이용권 내역" desc="충전(+)과 자동화 생성(−)이 시간순으로 표시돼요." />
                {usageQ.isLoading ? (
                    <CircularProgress size={22} sx={{ color: ACCENT }} />
                ) : usageItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        아직 사용 내역이 없어요.
                    </Typography>
                ) : (
                    <Stack divider={<Divider />} spacing={0}>
                        {usageItems.map((it, i) => (
                            <UsageRow key={`${it.kind}-${it.id ?? i}-${it.createdAt}`} item={it} />
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default AutomationPassPage;
